import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { ResponseWrapper } from 'ui/toast';
import { addNegativeToast, addPositiveToast, removeAllToasts } from 'modules/toastContainer';
import noOp from 'utils/noop';

import {
  GraphQLSnippet,
  SnippetFieldsInput,
  SnippetValidationFailure,
  UpdateSnippetMutationResult,
  ValidationData,
} from '../types';
import { getValidationData } from '../utils';

type Variables = {
  id: number;
  fields: SnippetFieldsInput;
};

export const UpdateSnippetMutation = gql`
  mutation UpdateSnippet($id: Long!, $fields: SnippetFieldsInput!) {
    updateSnippets(id: $id, fields: $fields) {
      success {
        content
        createdAt
        creatorUserId
        description
        id
        name
        positionalParameters
        projectId
        updatedAt
        updatedBy
      }
      failure {
        __typename
        ... on FieldInvalidFailure {
          field
          errorType {
            __typename
            ... on AnchorTagsWithJS {
              errorMsg
            }
            ... on HasCycle {
              errorMsg
            }
            ... on HasScriptTag {
              errorMsg
            }
            ... on IframeTagWithJS {
              errorMsg
            }
            ... on IllegalCharacters {
              errorMsg
            }
            ... on NotRenderable {
              errorMsg
            }
          }
        }
        ... on FieldNotProvidedFailure {
          field
        }
        ... on FieldTooLongFailure {
          field
        }
        ... on SnippetIdDoesNotExistFailure {
          Id
        }
        ... on SnippetParameterDuplicateFailure {
          field
        }
        ... on SnippetUpdateFailure {
          errorMsg
        }
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: (errors: ValidationData[]) => void;
  onSuccess: (snippet: GraphQLSnippet) => void;
};

export function useUpdateSnippet({ onComplete = noOp, onError = noOp, onSuccess = noOp }: Config) {
  const dispatch = useDispatch();
  const [updateSnippet, data] = useMutation<UpdateSnippetMutationResult, Variables>(
    UpdateSnippetMutation,
  );

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred')); // check Network tab (possible change to GraphQL schema)
    }

    if (!data.loading && data?.data?.updateSnippets) {
      const { failure, success } = data.data.updateSnippets;
      const errors: ValidationData[] = [];
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) || [];

      failures.forEach((err: SnippetValidationFailure) => {
        const validationData = getValidationData(err);

        dispatch(addNegativeToast(<ResponseWrapper>{validationData.errorMsg}</ResponseWrapper>));
        errors.push(validationData);
      });
      if (errors.length) {
        onError(errors);
      }

      if (success) {
        dispatch(removeAllToasts());
        dispatch(
          addPositiveToast(
            <ResponseWrapper>{`${success.name} was successfully updated.`}</ResponseWrapper>,
          ),
        );
        onSuccess(success);
      }

      // call the complete callback
      onComplete();
    }
  }, [data.loading]);

  return [updateSnippet, data] as const;
}
